import { useMemo } from 'react'
import type { ColDef } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import {
  DataGrid,
  GridTextCell,
  GridPercentCell,
} from '@/components/common/grid'
import { EditableOverrideCell } from './EditableOverrideCell'
import type { StoreModelMemberRead } from '@/types'

function GrossMarginCell(props: CustomCellRendererProps<StoreModelMemberRead>) {
  const value = props.value as number | null
  if (value == null)
    return <span className="text-sm text-muted-foreground">—</span>
  return <span className="text-sm text-foreground">{value.toFixed(1)}%</span>
}

function GroupNameCell(props: CustomCellRendererProps<StoreModelMemberRead>) {
  const value = props.value as string | null
  if (!value) return <span className="text-sm text-muted-foreground">—</span>
  return <span className="text-sm text-foreground">{value}</span>
}

function formatSales(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

function formatUnits(value: number): string {
  return value.toLocaleString('en-US')
}

interface MembersGridProps {
  members: StoreModelMemberRead[]
  onOverride: (
    storeId: string,
    field: 'total_sales' | 'total_units',
    value: number | null
  ) => void
  loading?: boolean
  onSortChange?: (
    sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>
  ) => void
}

export function MembersGrid({
  members,
  onOverride,
  loading,
  onSortChange,
}: MembersGridProps) {
  const columnDefs: ColDef<StoreModelMemberRead>[] = useMemo(
    () => [
      {
        field: 'store_number',
        headerName: 'Store #',
        flex: 1,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: GridTextCell,
      },
      {
        field: 'store_name',
        headerName: 'Name',
        flex: 2,
        minWidth: 160,
        cellRenderer: GridTextCell,
      },
      {
        field: 'region',
        headerName: 'Region',
        flex: 1,
        minWidth: 90,
        maxWidth: 120,
        cellRenderer: GridTextCell,
      },
      {
        field: 'allocation_percent',
        headerName: 'Alloc %',
        flex: 1,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: GridPercentCell,
      },
      {
        field: 'total_sales',
        headerName: 'Sales',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: EditableOverrideCell,
        cellRendererParams: {
          field: 'total_sales',
          overrideField: 'sales_override',
          formatValue: formatSales,
        },
      },
      {
        field: 'total_units',
        headerName: 'Units',
        flex: 1,
        minWidth: 90,
        maxWidth: 120,
        cellRenderer: EditableOverrideCell,
        cellRendererParams: {
          field: 'total_units',
          overrideField: 'units_override',
          formatValue: formatUnits,
        },
      },
      {
        field: 'gross_margin_percent',
        headerName: 'GM %',
        flex: 1,
        minWidth: 80,
        maxWidth: 100,
        cellRenderer: GrossMarginCell,
      },
      {
        field: 'store_group_name',
        headerName: 'Group',
        flex: 1,
        minWidth: 110,
        maxWidth: 160,
        cellRenderer: GroupNameCell,
      },
    ],
    []
  )

  return (
    <DataGrid
      rowData={members}
      columnDefs={columnDefs}
      context={{ onOverride }}
      loading={loading}
      noRowsMessage="No members found"
      rowHeight={52}
      onSortChanged={onSortChange}
    />
  )
}
